import { useEffect, useRef, useState } from 'react';

export interface AnimatedCounterProps {
  /** Target numeric value to animate to */
  value: number;
  /** Display format */
  format?: 'currency' | 'percent' | 'compact' | 'number';
  /** Text prepended before the formatted number */
  prefix?: string;
  /** Text appended after the formatted number */
  suffix?: string;
  /** Number of decimal places (defaults vary by format) */
  decimals?: number;
  /** Animation duration in ms (default 800) */
  duration?: number;
  /** Optional className for the wrapping span */
  className?: string;
}

/** Ease-out cubic: decelerates towards the end */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Format a number into compact notation like $1.25M, $312K, 2.4B, etc.
 */
function formatCompact(n: number, decimals?: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) {
    const d = decimals ?? 2;
    return `${(n / 1_000_000_000).toFixed(d)}B`;
  }
  if (abs >= 1_000_000) {
    const d = decimals ?? 2;
    return `${(n / 1_000_000).toFixed(d)}M`;
  }
  if (abs >= 1_000) {
    const d = decimals ?? (abs >= 100_000 ? 0 : 1);
    return `${(n / 1_000).toFixed(d)}K`;
  }
  const d = decimals ?? 0;
  return n.toFixed(d);
}

function formatValue(
  n: number,
  format: AnimatedCounterProps['format'],
  decimals?: number,
): string {
  switch (format) {
    case 'currency':
      return `$${formatCompact(n, decimals)}`;
    case 'percent': {
      const d = decimals ?? 0;
      return `${n.toFixed(d)}%`;
    }
    case 'compact':
      return formatCompact(n, decimals);
    case 'number':
    default: {
      const d = decimals ?? 0;
      return n.toFixed(d);
    }
  }
}

export function AnimatedCounter({
  value,
  format = 'number',
  prefix = '',
  suffix = '',
  decimals,
  duration = 800,
  className,
}: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const rafId = useRef<number | null>(null);
  const startTime = useRef<number | null>(null);
  const startValue = useRef(0);

  useEffect(() => {
    // Capture where we're starting from (current displayed value)
    startValue.current = displayValue;
    startTime.current = null;

    // Cancel any in-flight animation
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
    }

    function tick(timestamp: number) {
      if (startTime.current === null) {
        startTime.current = timestamp;
      }

      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      const current = startValue.current + (value - startValue.current) * eased;
      setDisplayValue(current);

      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        rafId.current = null;
      }
    }

    rafId.current = requestAnimationFrame(tick);

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
    // We intentionally only re-run when `value` or `duration` changes.
    // Including `displayValue` would create an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted = formatValue(displayValue, format, decimals);

  return (
    <span className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
